import { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import "../../AppPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../store/auth/hooks";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import useWebSocket from "react-use-websocket";
import { WEBSOCKET_URL } from "../../../store/config";
import {
  NOISE_FILTER_RUNNING,
  NOISE_FILTERING_QUEUED,
} from "../../../store/statuses";
import { loadClip } from "../utils/dataLoader";
import {
  getNavigationUrl,
  checkStatusWhitelist,
} from "../../../store/navigationUtil";

export default function NoiseFilterRunningPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { accessCode } = useAuth();
  const { caseId, clipId } = useParams();

  const [websocketRegistered, setWebsocketRegistered] = useState(false);
  const [chipStatus, setChipStatus] = useState("QUEUED");
  const [clip, setClip] = useState(null);

  const load = useCallback(async () => {
    const currClip = await loadClip(caseId, clipId);
    if (!currClip) {
      navigate(getNavigationUrl(caseId));
    }
    if (
      !checkStatusWhitelist(currClip, [
        NOISE_FILTERING_QUEUED,
        NOISE_FILTER_RUNNING,
      ])
    ) {
      navigate(getNavigationUrl(caseId, currClip));
    }
    if (currClip.stats === NOISE_FILTERING_QUEUED) {
      setChipStatus("QUEUED");
    } else {
      setChipStatus("RUNNING");
    }
    setClip(currClip);
  }, [caseId, clipId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Setup a websocket
   */
  const { sendMessage } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (event.data && event.data) {
        const data = JSON.parse(event.data);
        console.log("[WEBSOCKET]", data.data);
        if (
          data.command === "CLIP_STATUS" &&
          data.data.clipId === Number(clipId) &&
          data.data.clipStatus !== NOISE_FILTER_RUNNING &&
          data.data.clipStatus !== NOISE_FILTERING_QUEUED
        ) {
          console.log(
            "[GlimpseRunningPage][WebSocket] New Status",
            data.data.clipStatus
          );
          navigate(`/app/cases/${caseId}/clips/${clipId}`);
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      const payload = {
        command: "REGISTER",
        authorization: accessCode,
      };
      sendMessage(JSON.stringify(payload));
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} gap={2} p={2}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Box display="flex" flexWrap="wrap" columnGap={6} rowGap={1}>
            <Box display="flex" flexDirection="column" justifyContent="center">
              <Typography variant="h5">SPECTRAL NOISE FILTER</Typography>
            </Box>
            <Box display="flex" flexDirection="column">
              <Typography variant="caption">
                SPECTRAL NOISE FILTER STATUS
              </Typography>
              <Chip
                sx={{
                  alignSelf: "start",
                  backgroundColor:
                    chipStatus === "QUEUED" ? "#b000f0" : "#00b976",
                  color: theme.palette.common.white,
                }}
                label={chipStatus}
              />
            </Box>
          </Box>
        </Paper>
        <Alert
          severity="info"
          variant="filled"
          sx={{ bgcolor: theme.palette.primary.dark, boxShadow: 4 }}
        >
          <Box>
            The system is now running the Spectral Noise Filter on the audio
            files. This may take a few seconds.
          </Box>
          <Box>
            You may work on another clip or wait on this page for it to
            complete.
          </Box>
        </Alert>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          p: 1,
          mx: 1,
          mb: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={async () => navigate(`/app/cases/${caseId}/clips`)}
        >
          RETURN TO CLIPS
        </Button>
      </Paper>
      <ContentTitleBar name={clip?.description} label="Filter Running" />
    </Stack>
  );
}
